import React from 'react';
import { Flex, Text, Image, Spinner } from '@chakra-ui/react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEnvelope } from '@fortawesome/pro-regular-svg-icons';

import {
  IAdditionalInformation,
  IAffiliationDetails,
  IAuthor,
  IAuthorJournalInfo,
  IUser
} from '@publishing-engine/domain/types/AuthorType';
import { IAuthorWithEmail } from '@publishing-engine/domain/models';
import { useKeycloak } from 'src/components/AuthHOC';
import { orcidLogo } from '../../../pages/Branding/img';
import { Badge, Button, ButtonText } from '@morressier/alembic';
import useCurrentContextId from 'src/hooks/useCurrentContextId';
import UserEmail from 'src/components/User/UserEmail';
import { handleOpenUrl } from 'src/components/User/UpdateAffiliationDetails';
import AffiliationNotFound from './AffilationNotFound';
import RinggoldBadge from 'src/components/RinggoldBadge';
import ProxyUser from 'src/components/ProxyUser/ProxyUser';

interface IAuthorDetailsProps {
  user: IUser | IAuthorWithEmail | undefined;
  additionalInformation?: IAdditionalInformation;
  journals?: IAuthorJournalInfo[];
  isLoading?: boolean;
  isDeactivatedAccount?: boolean;
  isSubmittedAuthor?: boolean;
  affiliationDetails?: IAffiliationDetails;
  onSendEmail?: (email: string) => void;
  showActions?: boolean;
  isProxyUser?: boolean;
  isLoggedInUser?: boolean;
  isProxySession?: boolean;
  userId?: string;
  reviewId?: string;
  proxyRole?: 'author' | 'reviewer';
  proxyAuthorData?: IAuthor | null;
}

export const OrcidIcon = () => (
  <Image src={orcidLogo} width={'20px'} height={'20px'} alt={`ORCID Logo`} />
);

const formatAffiliationSummary = (
  affiliationDetails: IAffiliationDetails
): string => {
  const countryPart = [
    affiliationDetails.country?.trim(),
    affiliationDetails.countryCode?.trim()
      ? `(${affiliationDetails.countryCode.trim()})`
      : undefined
  ]
    .filter(Boolean)
    .join(' ');

  return [
    affiliationDetails.organizationName,
    affiliationDetails.department,
    affiliationDetails.streetAddress,
    affiliationDetails.stateOrRegion,
    affiliationDetails.city,
    affiliationDetails.postalCode,
    countryPart || undefined
  ]
    .map(part => part?.trim())
    .filter(Boolean)
    .join(', ');
};

const AuthorInfo: React.FC<IAuthorDetailsProps> = props => {
  const {
    user,
    additionalInformation,
    journals,
    isLoading,
    isSubmittedAuthor,
    affiliationDetails,
    onSendEmail,
    showActions,
    isProxyUser,
    isLoggedInUser,
    isProxySession,
    userId,
    reviewId,
    proxyRole,
    proxyAuthorData
  } = props;
  const { currentUser } = useKeycloak();
  const submissionContextId = useCurrentContextId() ?? '';
  const journal = journals?.find(journal => journal.id === submissionContextId);

  return (
    user && (
      <Flex direction="column" width="100%" gap={1}>
        <Flex
          gap={3}
          justifyContent="space-between"
          alignItems="center"
          flexWrap="wrap"
        >
          <Flex>
            <Text textStyle="headingGroup">
              {user?.firstName} {user?.lastName}
            </Text>

            {isLoading ? (
              <Spinner />
            ) : (
              <Flex gap={3} ml={2} align={'center'} flexWrap="wrap">
                {journal?.roles.map((role, index) => {
                  return (
                    <>
                      <Badge
                        key={index}
                        variant="neutral"
                        size="context"
                        data-cy={`badge-keyword-${index}`}
                        whiteSpace="pre-wrap"
                      >
                        <Text as="span" textTransform={'capitalize'}>
                          {role}
                        </Text>
                      </Badge>
                    </>
                  );
                })}
              </Flex>
            )}
          </Flex>
          {showActions && (
            <Flex
              gap={3}
              flexShrink={0}
              alignSelf={{ base: 'flex-start', lg: 'flex-start' }}
              flexWrap="wrap"
            >
              {onSendEmail && (
                <Button
                  width="fit-content"
                  variant="secondary"
                  iconLeft={<FontAwesomeIcon icon={faEnvelope} />}
                  onClick={() => onSendEmail(user.email ?? '')}
                >
                  Send Email
                </Button>
              )}
              {isProxyUser && !isLoggedInUser && !isProxySession && (
                <ProxyUser
                  user={{
                    userId: userId || '',
                    firstName: user?.firstName || '',
                    lastName: user?.lastName || ''
                  }}
                  isButton={true}
                  role={proxyRole}
                  reviewId={reviewId}
                  authorData={proxyAuthorData ?? undefined}
                />
              )}
            </Flex>
          )}
        </Flex>

        {currentUser.idp === 'ioppublishing' && (
          <Text>
            IOPP Membership:{' '}
            {additionalInformation?.ioppMembershipId || 'No data available'}
          </Text>
        )}
        {user?.orcid && (
          <Flex textStyle="textContext" gap={1}>
            <OrcidIcon />
            {user.orcid}
          </Flex>
        )}
        {user.email && (
          <UserEmail
            emailType="Primary Email"
            timeStamp={user.lastSentEmailTimeStamp}
            lastSentStatus={user.lastSentEmailStatus}
            email={user.email}
            enableIcon={Object.hasOwn(user, 'isEmailDelivered')}
            isEmailDelivered={user.isEmailDelivered}
            isUserDeactivated={props.isDeactivatedAccount || false}
          />
        )}

        {affiliationDetails ? (
          <Flex direction={'row'} gap={1}>
            <Text textStyle="headingItem">Affiliation:</Text>
            <Text textStyle="textBody">
              {affiliationDetails?.ringgoldId && <RinggoldBadge />}{' '}
              {formatAffiliationSummary(affiliationDetails)}
              {affiliationDetails.url?.trim() && (
                <>
                  {' '}
                  <ButtonText
                    onClick={() => handleOpenUrl(affiliationDetails.url || '')}
                    whiteSpace="normal"
                    wordBreak="break-all"
                  >
                    {affiliationDetails.url}
                  </ButtonText>
                </>
              )}
            </Text>
          </Flex>
        ) : (
          <AffiliationNotFound />
        )}
        {user?.degree && (
          <Flex direction={'row'} gap={1}>
            <Text textStyle="headingItem">Degree:</Text>
            <Text textStyle="textBody">{user.degree}</Text>
          </Flex>
        )}
        {user.secondaryEmail && !isSubmittedAuthor && (
          <UserEmail
            emailType="Secondary Email"
            timeStamp={user.secondaryEmailDetails?.emailTimeStamp}
            lastSentStatus={''}
            email={user.secondaryEmail}
            enableIcon={Object.hasOwn(user, 'isEmailDelivered')}
            isEmailDelivered={user.secondaryEmailDetails?.isEmailDelivered}
            isUserDeactivated={props.isDeactivatedAccount || false}
          />
        )}
        {user.ccEmail && !isSubmittedAuthor && (
          <UserEmail
            emailType="CC Email"
            timeStamp={user.ccEmailDetails?.emailTimeStamp}
            lastSentStatus={''}
            email={user.ccEmail}
            enableIcon={Object.hasOwn(user, 'isEmailDelivered')}
            isEmailDelivered={user.ccEmailDetails?.isEmailDelivered}
            isUserDeactivated={props.isDeactivatedAccount || false}
          />
        )}
      </Flex>
    )
  );
};

export default AuthorInfo;
